/**
 * Tax Documents Feature Types
 */

export type DteTypeCode = 33 | 34 | 52 | 56 | 61;

export type TaxDocumentStatus =
  | "draft"
  | "pending"
  | "submitted"
  | "accepted"
  | "rejected"
  | "cancelled";

export type ReceptorActionStatus =
  | "pending"
  | "accepted"
  | "goods_received"
  | "claimed_content"
  | "claimed_partial"
  | "claimed_total"
  | "auto_accepted";

export interface TaxDocumentListItem {
  id: string;
  documentType: string;
  documentTypeCode: string;
  folio: number;
  issueDate: string;
  recipientTaxId: string;
  recipientName: string | null;
  netAmount: string | null;
  taxAmount: string | null;
  totalAmount: string;
  status: TaxDocumentStatus;
  receptorStatus: ReceptorActionStatus | null;
  createdAt: string;
}

// Input types (used by DraftInput and other interfaces)
export interface LineItemInput {
  nombre: string;
  cantidad: number;
  precioUnitario: number;
  porcentajeDescuento?: number;
  exento?: boolean;
}

export interface GlobalDiscountInput {
  tipo: "D" | "R";
  descripcion: string;
  tipoValor: "%" | "$";
  valor: number;
  indicadorExencion?: 1 | 2;
}

export interface ReferenceInput {
  tipoDocRef: DteTypeCode;
  folioRef: number;
  fechaRef: string;
  codigoRef?: 1 | 2 | 3;
  razonRef?: string;
}

export interface ReceptorInput {
  rut: string;
  razonSocial: string;
  giro: string;
  direccion: string;
  comuna: string;
  ciudad?: string;
}

export interface DraftInput {
  documentType: DteTypeCode;
  issueDate: string;
  receptor: Partial<ReceptorInput>;
  lineItems: LineItemInput[];
  globalDiscounts?: GlobalDiscountInput[];
  references?: ReferenceInput[];
}

export interface TaxDocumentDetails extends TaxDocumentListItem {
  currency: string;
  dueDate: string | null;
  trackingId: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  statusDetail: string | null;
  authorityStatusCode: string | null;
  receptorStatus: ReceptorActionStatus | null;
  receptorActionCode: string | null;
  receptorActionAt: string | null;
  countryMetadata: {
    ted?: string;
    totals?: {
      montoNeto: number;
      iva: number;
      montoTotal: number;
      montoExento?: number;
    };
    siiStatus?: string;
    siiGloss?: string;
    draftInput?: DraftInput;
  };
  createdBy: string;
  updatedAt: string;
}

export interface ListTaxDocumentsResponse {
  items: TaxDocumentListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateTaxDocumentInput {
  documentType: DteTypeCode;
  issueDate: string;
  receptor: ReceptorInput;
  lineItems: LineItemInput[];
  globalDiscounts?: GlobalDiscountInput[];
  references?: ReferenceInput[];
  dispatchType?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  transportType?: 1 | 2 | 3;
}

export interface CreateTaxDocumentResponse {
  document: {
    id: string;
    documentType: DteTypeCode;
    documentTypeCode: string;
    folio: number;
    issueDate: string;
    status: "pending";
    recipientTaxId: string;
    recipientName: string;
    netAmount: number | null;
    taxAmount: number | null;
    totalAmount: number;
  };
}

export interface SaveDraftResponse {
  draft: {
    id: string;
    status: "draft";
    createdAt: string;
    updatedAt: string;
  };
}

export interface TaxDocumentDraft {
  id: string;
  status: "draft";
  documentTypeCode: string;
  issueDate: string;
  draftInput: DraftInput;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitToSiiResponse {
  result: {
    envelopeTrackingId: string;
    submittedAt: string;
    results: Array<{
      documentId: string;
      folio: number;
      success: boolean;
      trackingId?: string;
      error?: string;
    }>;
  };
}

export interface QuerySiiStatusResponse {
  status: {
    documentId: string;
    folio: number;
    status: TaxDocumentStatus;
    trackingId?: string;
    siiStatus?: string;
    siiGloss?: string;
    receptorStatus?: ReceptorActionStatus;
    receptorActionCode?: string;
    receptorActionAt?: string;
    updatedAt: string;
  };
}

// Display helpers
export const DTE_TYPE_LABELS: Record<DteTypeCode, string> = {
  33: "Factura Electrónica",
  34: "Factura Exenta",
  52: "Guía de Despacho",
  56: "Nota de Débito",
  61: "Nota de Crédito",
};

export const STATUS_LABELS: Record<TaxDocumentStatus, string> = {
  draft: "Borrador",
  pending: "Pendiente",
  submitted: "Enviado",
  accepted: "Aceptado",
  rejected: "Rechazado",
  cancelled: "Anulado",
};

export const STATUS_COLORS: Record<TaxDocumentStatus, string> = {
  draft: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  submitted: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-500",
};

export const RECEPTOR_STATUS_LABELS: Record<ReceptorActionStatus, string> = {
  pending: "Pendiente",
  accepted: "Aceptado",
  goods_received: "Mercaderías Recibidas",
  claimed_content: "Reclamo al Contenido",
  claimed_partial: "Reclamo Parcial",
  claimed_total: "Reclamo Total",
  auto_accepted: "Aceptado Tácitamente",
};

export const RECEPTOR_STATUS_COLORS: Record<ReceptorActionStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  accepted: "bg-green-100 text-green-800",
  goods_received: "bg-green-100 text-green-800",
  claimed_content: "bg-red-100 text-red-800",
  claimed_partial: "bg-orange-100 text-orange-800",
  claimed_total: "bg-red-100 text-red-800",
  auto_accepted: "bg-green-100 text-green-800",
};
